import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  LinearProgress,
  InputAdornment,
  IconButton,
  Paper,
  Avatar,
  FormGroup,
  CircularProgress,
  DialogTitle,
  DialogContent,
  Dialog,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Storage, API } from '../services/amplifyCompat';
import { createGroupAndOwner } from '../graphql/mutations';
import { getAllGroups } from '../graphql/queries';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../components/customHooks/translations';
import { useUserInfoContext } from '../hooks/use-user-info-context';
import { prepareImageForUpload } from '../utils/prepare-image-for-upload';

const Input = styled('input')`
  display: none;
`;

const SectionTitle = styled(Typography)`
  margin: 1.5em 0 0.5em 0;
`;

const CreateGroup = () => {
  const emptyCreateGroupForm = {
    group_name: '',
    group_description: '',
    group_image: '',
    is_public: true,
    private_password: '',
  };
  const { user } = useUserInfoContext();
  const [createGroupForm, setCreateGroupForm] = useState(emptyCreateGroupForm);
  const [allGroupNames, setAllGroupNames] = useState();
  const [avatarFile, setAvatarFile] = useState();
  const [avatarPreview, setAvatarPreview] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [emptyGroupNameError, setEmptyGroupNameError] = useState(false);
  const [groupNameTakenError, setGroupNameTakenError] = useState(false);
  const [emptyPasswordError, setEmptyPasswordError] = useState(false);
  const [avatarFileError, setAvatarFileError] = useState(false);
  const [createGroupSuccess, setCreateGroupSuccess] = useState(false);
  const [createGroupError, setCreateGroupError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const translation = useTranslation();

  //gets list of all group names to make sure submitted group isn't a duplicate
  useEffect(() => {
    const getGroups = async () => {
      const groupsRes = await API.graphql({
        query: getAllGroups,
      });
      const allGroups = groupsRes.data.getAllGroups;
      const groupNames = allGroups.map((group) => group.group_name);
      setAllGroupNames(groupNames);
    };
    getGroups();
  }, []);

  const updateForm = (e) => {
    setCreateGroupForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    if (e.target.name === 'group_name') {
      if (e.target.value) {
        setEmptyGroupNameError(false);
      }
    }
  };

  const handleMouseDownPassword = (e) => {
    e.preventDefault();
  };

  /** functions for uploading an icon */

  const handleAvatarChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setAvatarFile(null);
      return;
    }
    try {
      const imageFile = await prepareImageForUpload(e.target.files[0]);
      const previewLink = URL.createObjectURL(imageFile);
      setAvatarFile(imageFile);
      setAvatarPreview(previewLink);
      setAvatarFileError(false);
    } catch (error) {
      if (error?.code === 'UNSUPPORTED_IMAGE') {
        setAvatarFileError(true);
      } else {
        throw error;
      }
    }
  };

  /** functions adding the group */

  const checkRequiredFields = () => {
    const { group_name, private_password, is_public } = createGroupForm;

    //checks for empty or existing group name
    if (group_name === '') {
      throw new Error('Empty group name');
    } else if (allGroupNames.includes(group_name)) {
      throw new Error('Group name is taken');
    }
    //check for empty password for private groups
    if (!is_public && private_password === '') {
      throw new Error('Empty password');
    }
  };

  const submitGroup = async () => {
    try {
      setCreateGroupError(false);
      checkRequiredFields();
      setIsLoading(true);
      const { group_name, group_description, is_public, private_password } =
        createGroupForm;
      //if user uploaded an icon image, get the group name to upload the group avatar image
      let imageKey = 'groupIcons/'.concat(group_name);
      let iconLink = null;
      if (avatarFile) {
        let imageType = avatarFile.type;
        const uploadedIcon = await Storage.put(imageKey, avatarFile, {
          contentType: imageType,
        });
        iconLink = uploadedIcon.url;
      }
      //create the group
      await API.graphql({
        query: createGroupAndOwner,
        variables: {
          owner_user_id: user.user_id,
          group_name: group_name,
          group_description: group_description,
          group_image: iconLink,
          is_public: is_public,
          private_password: private_password,
        },
      });
      const groupName = group_name;
      //clear form and related states
      setCreateGroupForm(emptyCreateGroupForm);
      setEmptyGroupNameError(false);
      setGroupNameTakenError(false);
      setAvatarPreview();
      //render success message
      setCreateGroupSuccess(true);
      setTimeout(() => {
        navigate(`/group-profile/${encodeURIComponent(groupName)}`);
      }, 2000);
    } catch (e) {
      setIsLoading(false);
      const errorMsg = e?.message || '';
      if (errorMsg.includes('Empty group name')) {
        setEmptyGroupNameError(true);
      } else if (errorMsg.includes('Group name is taken')) {
        setGroupNameTakenError(true);
      } else if (errorMsg.includes('Empty password')) {
        setEmptyPasswordError(true);
      } else {
        setCreateGroupError(true);
      }
    }
  };

  //once action has been successfully submitted, set loading state to false to remove progress bar
  useEffect(() => {
    createGroupSuccess && setIsLoading(false);
  }, [createGroupSuccess]);

  return (
    <>
      {allGroupNames ? (
        <>
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              variant="h1"
              sx={{ mt: { xs: '1.5em', sm: '0em' }, mb: '1.5em' }}
            >
              {translation.createGroupTitle}
            </Typography>
          </Box>
          <Box
            component={Paper}
            sx={{
              display: 'flex',
              p: '1em',
              justifyContent: 'center',
              flexDirection: 'column',
              overflow: 'initial',
              backgroundColor: '#FFFFFF',
            }}
          >
            <FormGroup>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1em',
                  maxWidth: '800px',
                  margin: '0 auto',
                  width: '100%',
                }}
              >
                {avatarPreview ? (
                  <Avatar
                    variant="rounded"
                    sx={{ height: 150, width: 150, alignSelf: 'center' }}
                    alt="Uploaded Group Icon"
                    src={avatarPreview}
                  />
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{
                      height: 150,
                      width: 150,
                    }}
                  />
                )}
                <label htmlFor="group-icon-image">
                  <Input
                    accept="image/*"
                    id="group-icon-image"
                    type="file"
                    onChange={handleAvatarChange}
                  />
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{ m: { xs: '1.5em 0 0', md: '0' } }}
                  >
                    {translation.uploadGroupIcon}
                  </Button>
                </label>
                {avatarFileError && (
                  <Typography variant="subtitle2" color="error">
                    {translation.imageValidationError}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1em',
                  mt: '1em',
                  maxWidth: '800px',
                  margin: '0 auto',
                  width: '100%',
                }}
              >
                <>
                  {' '}
                  <SectionTitle variant="h2">
                    {translation.groupName}
                  </SectionTitle>
                  <TextField
                    required
                    label={translation.groupName}
                    name="group_name"
                    InputLabelProps={{ shrink: true }}
                    value={createGroupForm.group_name}
                    error={emptyGroupNameError || groupNameTakenError}
                    helperText={
                      (emptyGroupNameError &&
                        'Group Name field must be completed') ||
                      (groupNameTakenError &&
                        'A group already exists with the given name')
                    }
                    onChange={updateForm}
                    sx={{
                      width: '100%',
                    }}
                  />
                </>
                <>
                  <SectionTitle variant="h2">
                    {translation.groupDescription}
                  </SectionTitle>
                  <TextField
                    multiline
                    rows={6}
                    label={translation.groupDescription}
                    name="group_description"
                    value={createGroupForm.group_description}
                    InputLabelProps={{ shrink: true }}
                    onChange={updateForm}
                    sx={{
                      width: '100%',
                    }}
                  />
                </>
                <>
                  {' '}
                  <SectionTitle variant="h2">
                    {translation.groupPrivacy}
                  </SectionTitle>
                  <RadioGroup
                    aria-labelledby="group-privacy-label"
                    defaultValue={createGroupForm.is_public}
                    name="group-privacy-options"
                    required
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={translation.public}
                      checked={createGroupForm.is_public}
                      onClick={() =>
                        setCreateGroupForm({
                          ...createGroupForm,
                          is_public: true,
                          private_password: '',
                        })
                      }
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={translation.private}
                      checked={!createGroupForm.is_public}
                      onClick={() =>
                        setCreateGroupForm({
                          ...createGroupForm,
                          is_public: false,
                        })
                      }
                    />
                  </RadioGroup>
                  {/* only show private password text field if user selects private group */}
                  {!createGroupForm.is_public && (
                    <TextField
                      label="password"
                      name="private_password"
                      value={createGroupForm.private_password}
                      InputLabelProps={{ shrink: true }}
                      onChange={updateForm}
                      sx={{ mt: '1.5em' }}
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={emptyPasswordError}
                      helperText={
                        emptyPasswordError &&
                        'Private groups must have a password'
                      }
                    ></TextField>
                  )}
                </>
              </Box>
              {isLoading && (
                <LinearProgress
                  sx={{ width: '100%', mt: '1.5em' }}
                  color="primary"
                  variant="indeterminate"
                />
              )}
              {createGroupError && (
                <Typography
                  variant="subtitle2"
                  color="error"
                  sx={{ mt: '1.5em', textAlign: 'center' }}
                >
                  {translation.createStudentAccount?.unexpectedError ||
                    'An unexpected error occurred. Please try again later.'}
                </Typography>
              )}
              <Button
                sx={{
                  mt: '2em',
                  padding: '1em',
                  width: '100%',
                  alignSelf: 'center',
                  maxWidth: '800px',
                }}
                variant="contained"
                type="submit"
                onClick={submitGroup}
                disabled={isLoading}
              >
                {translation.createGroup}
              </Button>
            </FormGroup>
            <Dialog
              aria-labelledby="success-dialog"
              PaperProps={{
                sx: {
                  p: '1em',
                  display: 'flex',
                  justifyContent: 'center',
                  textAlign: 'center',
                  alignItems: 'center',
                },
              }}
              open={createGroupSuccess}
            >
              <DialogTitle sx={{ textAlign: 'center' }}>
                {' '}
                {translation.success}
              </DialogTitle>
              <DialogContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Typography>{translation.redirectGroupPage}</Typography>
                <CircularProgress sx={{ mt: '2em' }} />
              </DialogContent>
            </Dialog>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '5em' }}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

export default CreateGroup;
